import * as React from 'react';
import { Loader2, Search, X } from 'lucide-react';
import { cva } from 'class-variance-authority';

import { Button } from '@dfq/shared/components/ui/button';
import { cn } from '@dfq/shared/lib/utils';

/** Fields the capsule actually renders from a product suggestion. */
export interface SearchSuggestion {
  id: number;
  name: string;
  slug: string;
  category_slug: string | null;
  primary_image_url: string | null;
  base_price: string;
  base_price_unit: string;
}

/** Fetcher signature — parent provides; capsule owns the 300 ms debounce. */
export type FetchSuggestionsFn = (query: string) => Promise<SearchSuggestion[]>;

function formatStartingPrice(item: SearchSuggestion): string {
  const price = parseFloat(item.base_price);
  const formatted = isNaN(price) ? item.base_price : `$${price.toFixed(2)}`;
  return item.base_price_unit === 'sqft'
    ? `from ${formatted} / sq ft`
    : formatted;
}

export interface SearchBarProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onSubmit'> {
  /** Initial/controlled value for the text input. */
  defaultValue?: string;
  /** Placeholder text shown when the input is empty. Defaults to "Search products…". */
  placeholder?: string;
  fetchSuggestions: FetchSuggestionsFn;
  /** Emitted when the user submits a query (Enter with no highlighted item, or footer link). */
  onSubmit: (query: string) => void;
  /** Emitted when the user clicks or keys Enter on a suggestion. */
  onSelectSuggestion: (suggestion: SearchSuggestion) => void;
  /** Emitted when the user clears the input (× button). */
  onClear: () => void;
}

function suggestionId(listboxId: string, index: number): string {
  return `${listboxId}-option-${index}`;
}

const suggestionRowVariants = cva(
  'flex cursor-pointer items-center gap-3 px-3 py-2 text-sm outline-none transition-colors',
  {
    variants: {
      highlighted: {
        true: 'bg-accent text-accent-foreground',
        false: 'hover:bg-muted',
      },
    },
    defaultVariants: { highlighted: false },
  },
);

interface MobileOverlayProps {
  inputId: string;
  listboxId: string;
  inputValue: string;
  suggestions: SearchSuggestion[];
  isLoading: boolean;
  isOpen: boolean;
  highlightedIndex: number;
  placeholder: string;
  onInputChange: (value: string) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onSelectItem: (item: SearchSuggestion, index: number) => void;
  onSubmitQuery: () => void;
  onClearInput: () => void;
  onClose: () => void;
}

function MobileOverlay({
  inputId,
  listboxId,
  inputValue,
  suggestions,
  isLoading,
  isOpen,
  highlightedIndex,
  placeholder,
  onInputChange,
  onKeyDown,
  onSelectItem,
  onSubmitQuery,
  onClearInput,
  onClose,
}: MobileOverlayProps) {
  const overlayInputRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    overlayInputRef.current?.focus();
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Search"
      className="fixed inset-0 z-50 flex flex-col bg-background p-4"
    >
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            aria-hidden="true"
            className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
          />
          <input
            ref={overlayInputRef}
            id={`${inputId}-mobile`}
            type="search"
            role="combobox"
            aria-expanded={isOpen}
            aria-autocomplete="list"
            aria-controls={`${listboxId}-mobile`}
            aria-activedescendant={
              highlightedIndex >= 0
                ? suggestionId(`${listboxId}-mobile`, highlightedIndex)
                : undefined
            }
            autoComplete="off"
            value={inputValue}
            placeholder={placeholder}
            onChange={(e) => onInputChange(e.target.value)}
            onKeyDown={onKeyDown}
            className={cn(
              'h-9 w-full min-w-0 rounded-md border border-input bg-transparent py-1 pl-9 pr-8 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground md:text-sm',
              'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
            )}
          />
          {inputValue.length > 0 && (
            <button
              type="button"
              aria-label="Clear search"
              onClick={onClearInput}
              className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X aria-hidden="true" className="size-4" />
            </button>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="shrink-0"
        >
          Cancel
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-6">
          <Loader2 aria-label="Loading suggestions" className="size-5 animate-spin text-muted-foreground" />
        </div>
      )}

      {!isLoading && isOpen && suggestions.length > 0 && (
        <ul
          id={`${listboxId}-mobile`}
          role="listbox"
          aria-label="Search suggestions"
          className="mt-2 overflow-y-auto rounded-md border bg-popover shadow-md"
        >
          {suggestions.map((item, i) => (
            <li
              key={item.id}
              id={suggestionId(`${listboxId}-mobile`, i)}
              role="option"
              aria-selected={highlightedIndex === i}
              className={suggestionRowVariants({ highlighted: highlightedIndex === i })}
              onMouseDown={(e) => {
                e.preventDefault();
                onSelectItem(item, i);
              }}
            >
              {item.primary_image_url ? (
                <img
                  src={item.primary_image_url}
                  alt=""
                  aria-hidden="true"
                  className="size-10 shrink-0 rounded object-cover"
                />
              ) : (
                <div aria-hidden="true" className="size-10 shrink-0 rounded bg-muted" />
              )}
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{item.name}</p>
                <p className="text-xs text-muted-foreground">{formatStartingPrice(item)}</p>
              </div>
            </li>
          ))}
          {inputValue.trim().length > 0 && (
            <li
              role="option"
              aria-selected={false}
              className="border-t px-3 py-2 text-sm text-primary hover:bg-muted cursor-pointer"
              onMouseDown={(e) => {
                e.preventDefault();
                onSubmitQuery();
              }}
            >
              See all results for <strong>{inputValue.trim()}</strong>
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/**
 * Global site search input with typeahead suggestions.
 *
 * Pure capsule — owns 300 ms debounce + ARIA combobox state + mobile overlay.
 * Receives fetchSuggestions and all callbacks from the parent container.
 *
 * SF-SEARCH-003.
 */
export function SearchBar({
  defaultValue = '',
  placeholder = 'Search products…',
  fetchSuggestions,
  onSubmit,
  onSelectSuggestion,
  onClear,
  className,
  ...rest
}: SearchBarProps) {
  const [inputValue, setInputValue] = React.useState(defaultValue);
  const [suggestions, setSuggestions] = React.useState<SearchSuggestion[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const inputRef = React.useRef<HTMLInputElement>(null);
  const debounceTimer = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = React.useRef<AbortController | null>(null);

  const inputId = React.useId();
  const listboxId = React.useId();

  const handleInputChange = React.useCallback(
    (value: string) => {
      setInputValue(value);
      setHighlightedIndex(-1);

      if (debounceTimer.current) clearTimeout(debounceTimer.current);

      if (value.trim().length === 0) {
        setSuggestions([]);
        setIsOpen(false);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);

      debounceTimer.current = setTimeout(async () => {
        abortRef.current?.abort();
        abortRef.current = new AbortController();

        try {
          const results = await fetchSuggestions(value.trim());
          setSuggestions(results);
          setIsOpen(results.length > 0);
        } catch {
          setSuggestions([]);
          setIsOpen(false);
        } finally {
          setIsLoading(false);
        }
      }, 300);
    },
    [fetchSuggestions],
  );

  const clearInput = React.useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    setInputValue('');
    setSuggestions([]);
    setIsOpen(false);
    setIsLoading(false);
    setHighlightedIndex(-1);
    onClear();
    inputRef.current?.focus();
  }, [onClear]);

  const selectItem = React.useCallback(
    (item: SearchSuggestion) => {
      setInputValue(item.name);
      setIsOpen(false);
      setHighlightedIndex(-1);
      onSelectSuggestion(item);
    },
    [onSelectSuggestion],
  );

  const submitQuery = React.useCallback(() => {
    const q = inputValue.trim();
    if (!q) return;
    setIsOpen(false);
    setHighlightedIndex(-1);
    onSubmit(q);
  }, [inputValue, onSubmit]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) =>
          isOpen ? Math.min(prev + 1, suggestions.length - 1) : prev,
        );
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, -1));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (highlightedIndex >= 0 && suggestions[highlightedIndex]) {
          selectItem(suggestions[highlightedIndex]);
        } else {
          submitQuery();
        }
      } else if (e.key === 'Escape') {
        setIsOpen(false);
        setHighlightedIndex(-1);
        inputRef.current?.focus();
      }
    },
    [isOpen, suggestions, highlightedIndex, selectItem, submitQuery],
  );

  const containerRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    function handlePointerDown(e: PointerEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setHighlightedIndex(-1);
      }
    }
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, []);

  React.useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, []);

  const closeMobileOverlay = React.useCallback(() => {
    setMobileOpen(false);
    setInputValue('');
    setSuggestions([]);
    setIsOpen(false);
    setIsLoading(false);
    setHighlightedIndex(-1);
    onClear();
  }, [onClear]);

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <MobileOverlay
          inputId={inputId}
          listboxId={listboxId}
          inputValue={inputValue}
          suggestions={suggestions}
          isLoading={isLoading}
          isOpen={isOpen}
          highlightedIndex={highlightedIndex}
          placeholder={placeholder}
          onInputChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onSelectItem={(item) => {
            setMobileOpen(false);
            selectItem(item);
          }}
          onSubmitQuery={() => {
            setMobileOpen(false);
            submitQuery();
          }}
          onClearInput={clearInput}
          onClose={closeMobileOverlay}
        />
      )}

      <div
        ref={containerRef}
        className={cn('relative', className)}
        {...rest}
      >
        {/* Mobile icon trigger — visible only below sm */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label="Open search"
          className="flex sm:hidden"
          onClick={() => setMobileOpen(true)}
        >
          <Search aria-hidden="true" className="size-5" />
        </Button>

        {/* Desktop inline input — hidden below sm */}
        <div className="relative hidden sm:flex sm:items-center">
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              submitQuery();
            }}
          >
            {isLoading ? (
              <Loader2
                aria-label="Loading suggestions"
                className="absolute left-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground"
              />
            ) : (
              <Search
                aria-hidden="true"
                className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
              />
            )}

            <input
              ref={inputRef}
              id={inputId}
              type="search"
              role="combobox"
              aria-expanded={isOpen}
              aria-autocomplete="list"
              aria-controls={listboxId}
              aria-activedescendant={
                highlightedIndex >= 0
                  ? suggestionId(listboxId, highlightedIndex)
                  : undefined
              }
              autoComplete="off"
              value={inputValue}
              placeholder={placeholder}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              className={cn(
                'h-9 w-64 min-w-0 rounded-md border border-input bg-transparent py-1 pl-9 pr-8 text-sm shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground',
                'focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50',
              )}
            />

            {inputValue.length > 0 && (
              <button
                type="button"
                aria-label="Clear search"
                onClick={clearInput}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-sm text-muted-foreground hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X aria-hidden="true" className="size-4" />
              </button>
            )}
          </form>
        </div>

        {/* Dropdown listbox — desktop only */}
        {isOpen && suggestions.length > 0 && (
          <ul
            id={listboxId}
            role="listbox"
            aria-label="Search suggestions"
            className="absolute left-0 top-full z-50 mt-1 hidden w-80 overflow-y-auto rounded-md border bg-popover shadow-md sm:block"
          >
            {suggestions.map((item, i) => (
              <li
                key={item.id}
                id={suggestionId(listboxId, i)}
                role="option"
                aria-selected={highlightedIndex === i}
                className={suggestionRowVariants({ highlighted: highlightedIndex === i })}
                onMouseDown={(e) => {
                  e.preventDefault();
                  selectItem(item);
                }}
              >
                {item.primary_image_url ? (
                  <img
                    src={item.primary_image_url}
                    alt=""
                    aria-hidden="true"
                    className="size-10 shrink-0 rounded object-cover"
                  />
                ) : (
                  <div aria-hidden="true" className="size-10 shrink-0 rounded bg-muted" />
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{item.name}</p>
                  <p className="text-xs text-muted-foreground">{formatStartingPrice(item)}</p>
                </div>
              </li>
            ))}
            {inputValue.trim().length > 0 && (
              <li
                role="option"
                aria-selected={false}
                className="border-t px-3 py-2 text-sm text-primary hover:bg-muted cursor-pointer"
                onMouseDown={(e) => {
                  e.preventDefault();
                  submitQuery();
                }}
              >
                See all results for <strong>{inputValue.trim()}</strong>
              </li>
            )}
          </ul>
        )}
      </div>
    </>
  );
}
